/**
 * MCP access (S-15) — this control plane as a remote MCP server.
 *
 * The server is the Console's own origin plus `/mcp` (Streamable HTTP), so
 * the snippets follow wherever this page is served from: locally that reads
 * `http://127.0.0.1:7717/mcp`, on a hosted deploy `https://agents.example.com/mcp`
 * — no docker, no extra port. The durable credential is never embedded in the
 * public HTML; this screen fetches it through the authenticated Console API.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { CheckIcon, CopyIcon } from '@primer/octicons-react';
import { Button, Flash } from '@primer/react';

import { mutationContext } from '../../client/data-source.js';
import { useStore } from '../store-context.js';

function CopyBlock({ label, text }: { readonly label: string; readonly text: string }): ReactNode {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    const done = (): void => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    };
    // The clipboard API needs a user activation and a permission; when either
    // is missing (an automation click, a stricter browser) the selection path
    // still works — and silently copying nothing is the one failure this
    // button must not have.
    const fallback = (): void => {
      const area = document.createElement('textarea');
      area.value = text;
      document.body.append(area);
      area.select();
      document.execCommand('copy');
      area.remove();
      done();
    };
    try {
      navigator.clipboard.writeText(text).then(done, fallback);
    } catch {
      fallback();
    }
  };
  return (
    <section className="mcp-block">
      <div className="mcp-block-head">
        <strong>{label}</strong>
        <Button size="small" leadingVisual={copied ? CheckIcon : CopyIcon} onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="mcp-snippet">{text}</pre>
    </section>
  );
}

export function McpAccessScreen(): ReactNode {
  const store = useStore();
  const [access, setAccess] = useState<{ readonly token: string; readonly persistent: boolean } | null>(null);
  const [credentialState, setCredentialState] = useState<'loading' | 'ready' | 'error'>('loading');
  const url = `${globalThis.location.origin}/mcp`;

  useEffect(() => {
    let active = true;
    void store.data.mcpAccessToken(mutationContext(store.config.actorId)).then((value) => {
      if (!active) return;
      setAccess(value);
      setCredentialState('ready');
    }).catch(() => {
      if (active) setCredentialState('error');
    });
    return () => { active = false; };
  }, [store]);

  const session = access?.token ?? '';

  const claudeCommand = `claude mcp add --transport http autonomous-team ${url} --header "Authorization: Bearer ${session}"`;

  /*
   * Codex reads the bearer from an environment variable, never from the
   * config — so the one-liner exports it first, and the variable must be
   * present on every later `codex` run (put the export in your shell rc to
   * make it stick).
   */
  const codexCommand = `export AUTONOMOUS_TEAM_MCP_TOKEN="${session}" && codex mcp add autonomous-team --url ${url} --bearer-token-env-var AUTONOMOUS_TEAM_MCP_TOKEN`;

  const mcpJson = JSON.stringify(
    {
      mcpServers: {
        'autonomous-team': {
          type: 'http',
          url,
          headers: { Authorization: `Bearer ${session}` },
        },
      },
    },
    null,
    2,
  );

  const codexToml = [
    `# export AUTONOMOUS_TEAM_MCP_TOKEN="${session}"`,
    '[mcp_servers."autonomous-team"]',
    `url = ${JSON.stringify(url)}`,
    'bearer_token_env_var = "AUTONOMOUS_TEAM_MCP_TOKEN"',
  ].join('\n');

  return (
    <div className="screen-body mcp-access">
      <h2>Operate this platform from your own agent</h2>
      <p className="mcp-intro">
        This control plane is itself an MCP server at <code>{url}</code>. Point any MCP client at it — Claude
        Code, Codex, a desktop assistant — and it can do everything this Console can: read every team’s board,
        author cards, talk to agents, edit skills and tokens, save and activate team versions. Two tools
        arrive: <code>api_guide</code> (the map — call it first) and <code>api_request</code> (every endpoint).
        The URL follows this page’s own address, so on a hosted deploy the same screen hands out the domain
        form. Full administrative access — share it with nothing you would not hand this Console to.
      </p>
      {access?.persistent === true ? (
        <Flash variant="success" className="mcp-notice">
          This is a persistent MCP-only credential. It survives daemon and container restarts and changes only
          when <code>AUTONOMOUS_TEAM_MCP_TOKEN</code> is rotated by an operator.
        </Flash>
      ) : access === null ? null : (
        <Flash variant="warning" className="mcp-notice">
          This installation has no <code>AUTONOMOUS_TEAM_MCP_TOKEN</code>; the generated credential rotates on
          every daemon restart.
        </Flash>
      )}

      {credentialState === 'loading' ? (
        <Flash className="mcp-notice">Loading persistent MCP credential…</Flash>
      ) : null}
      {credentialState === 'error' ? (
        <Flash variant="danger" className="mcp-notice">
          No access token is available. Sign in again, then refresh this page.
        </Flash>
      ) : null}
      {credentialState === 'ready' && access !== null ? (
        <>
          <CopyBlock label="Claude Code — one command" text={claudeCommand} />
          <CopyBlock label="Codex — one command (the token lives in the env var; add the export to your shell rc)" text={codexCommand} />
          <CopyBlock label=".mcp.json — Claude Code and most MCP clients" text={mcpJson} />
          <CopyBlock label="config.toml — Codex, by hand" text={codexToml} />
        </>
      ) : null}
    </div>
  );
}
